import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

/**
 * useArticleEngagement — tracks scroll depth and time on page for a single article.
 *
 * - Uses IntersectionObserver on sentinel <div>s placed at 10%, 25%, 50%, 75%, and 100%
 *   of the article body height to track the maximum scroll depth reached.
 * - Fires a POST to /api/analytics/engagement on pagehide and visibilitychange:hidden
 *   so data is captured even when the user navigates away without a beforeunload event.
 * - Skips entirely for authenticated (admin/staff) users.
 * - Deduplicates per session — the server will keep whichever record has the higher
 *   scroll depth if the user visits the same article multiple times in one session.
 */
export function useArticleEngagement(articleId: string | undefined) {
  const { data: user } = useQuery<{ id?: string } | null>({ queryKey: ["/api/auth/user"] });
  const isAuthed = !!(user as { id?: string } | null)?.id;

  const scrollDepthRef = useRef(0);
  const startTimeRef = useRef(Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    if (!articleId || isAuthed) return;

    const sessionId =
      sessionStorage.getItem("fm_session_id") ??
      (() => {
        const id = crypto.randomUUID();
        sessionStorage.setItem("fm_session_id", id);
        return id;
      })();

    startTimeRef.current = Date.now();
    scrollDepthRef.current = 0;
    firedRef.current = false;

    // ── Scroll sentinels ───────────────────────────────────────────────────
    // We create five invisible divs positioned at 10%, 25%, 50%, 75%, and 100%
    // of the document body height. When each enters the viewport we update the
    // maximum scroll depth reached.
    const DEPTHS = [10, 25, 50, 75, 100];
    const sentinels: HTMLElement[] = [];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const depth = Number((entry.target as HTMLElement).dataset.depth ?? 0);
            if (depth > scrollDepthRef.current) scrollDepthRef.current = depth;
          }
        }
      },
      { threshold: 0 },
    );

    const placeSentinels = () => {
      const bodyHeight = document.documentElement.scrollHeight;
      for (const pct of DEPTHS) {
        const el = document.createElement("div");
        el.dataset.depth = String(pct);
        el.style.cssText = `position:absolute;left:0;width:1px;height:1px;pointer-events:none;`;
        el.style.top = `${Math.round((bodyHeight * pct) / 100)}px`;
        document.body.appendChild(el);
        sentinels.push(el);
        observer.observe(el);
      }
    };

    // Small delay to allow article content to render/expand before measuring
    const sentinelTimeout = setTimeout(placeSentinels, 800);

    // ── Fire function ──────────────────────────────────────────────────────
    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;

      const timeOnPageSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
      const bounced = timeOnPageSeconds < 10 && scrollDepthRef.current < 20;

      // Use sendBeacon for reliability on page unload
      const payload = JSON.stringify({
        articleId,
        sessionId,
        scrollDepth: scrollDepthRef.current,
        timeOnPageSeconds,
        bounced,
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/analytics/engagement", new Blob([payload], { type: "application/json" }));
      } else {
        fetch("/api/analytics/engagement", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => undefined);
      }
    };

    // ── Event listeners ────────────────────────────────────────────────────
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") fire();
    };
    const onPageHide = () => fire();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      clearTimeout(sentinelTimeout);
      observer.disconnect();
      sentinels.forEach(s => s.remove());
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      // Also fire when the component unmounts (SPA navigation)
      fire();
    };
  }, [articleId, isAuthed]);
}
